import {
  IEditableObject,
  IEditData,
  IEditFrame,
  IImageObject,
  isText,
  ITemplateImage,
  ITemplateSet,
  ITextObject,
  IImageMaterial,
  consts,
  isImage,
  emptyEditSet,
} from "@/services/Models";
import { reactive } from "vue";
import { UndoRedo } from "@/stores/modules/undo";
import { ResourceStore } from "@/stores/modules/resource";
import { findParent, yyyyMMddHHmmssSSS } from "@/lib/shared";
import { v4 as uuid } from "uuid";
export interface IEditor {
  getImage: () => Promise<string | undefined>;
  getSelectedElement: () => Element | undefined;
}

interface IFrameWithEditor {
  index: number;
  frame: ITemplateImage;
  edit: IEditFrame;
  editor: IEditor | undefined;
}

interface EditState {
  template: ITemplateSet;
  editData: IEditData;
  frameWithEdit: IFrameWithEditor[];
  selectedFrameIndex: number;
  selectedFrame: ITemplateImage | undefined;
  selectedEdit: IEditFrame | undefined;
  selectedObject: IEditableObject | undefined;
}

export const useEditStore = (
  set: ITemplateSet,
  editData: IEditData,
  undo: UndoRedo,
  resource: ResourceStore
) => {
  // 編集データロード時点でオブジェクトにユニークKeyがついてない場合は更新する
  editData.frames.forEach((frame) =>
    frame.items.forEach((item) => {
      if (!item.key) {
        item.key = uuid().toString();
      }
    })
  );

  document.execCommand("defaultParagraphSeparator", false, "p");
  // 編集ストアの状態を現在の編集データで上書きする
  const updateEditState = (editData: IEditData) => {
    // MEMO 上書きするとundoが消えちゃう設定したいのはIDだけ
    // state.editData = editData;
    state.editData.id = editData.id;
    if (editData.editset !== undefined) {
      state.editData.editset = editData.editset;
    }
    for (const frame of state.editData.frames) {
      const newFrame = editData.frames.find((x) => x.order == frame.order);
      if (!newFrame) continue;
      frame.id = newFrame.id;
      frame.editDataID = newFrame.editDataID;
      for (const item of frame.items) {
        const newItem = newFrame.items.find((x) => x.key == item.key);
        if (!newItem) continue;
        item.id = newItem.id;
        item.editFrameID = newItem.editFrameID;
      }
    }
    // 画像フレームと編集フレームのペアを更新
    state.frameWithEdit
      .sort((a, b) => a.index - b.index)
      .forEach((fe) => {
        // お互いのコマ順序の一致でペアを判断する
        fe.edit = state.editData.frames.find((f) => f.order == fe.edit.order)!;
      });
    // 選択状態を更新
    state.selectedEdit = state.editData.frames.find(
      (f) => f.order == state.selectedFrameIndex
    );
    state.selectedObject = undefined;
  };
  // 編集状態
  const state = reactive<EditState>({
    template: set,
    editData: editData,
    frameWithEdit: Array.from(set.frames)
      .sort((a, b) => a.order - b.order)
      .map((x) => ({
        index: x.order,
        frame: x,
        edit: editData.frames.find((f) => f.order == x.order)!,
        editor: undefined,
      })),
    selectedFrameIndex: 0,
    selectedFrame: set.frames.find((f) => f.order == 0),
    selectedEdit: editData.frames.find((f) => f.order == 0),
    selectedObject: undefined,
  });

  // FIXME 編集中の各フレームの画像を取得したいけど、コンポーネントから分離するのが難しい。
  // 苦肉の策としてコンポーネントそのものを格納しちゃうんだけど
  // IEditorインタフェースとして受け取っておいていい案があったら差し替えられるようにしておく
  const setEditor = (frameIndex: number, editor: IEditor) => {
    const fe = state.frameWithEdit.find((x) => x.index == frameIndex);
    if (fe === undefined) return;
    fe.editor = editor;
  };

  // 現在選択中のフレーム
  const selectFrame = (index: number) => {
    state.selectedFrameIndex = index;
    state.selectedFrame = state.template.frames.find(
      (f) => f.order == state.selectedFrameIndex
    );
    state.selectedEdit = state.editData.frames.find(
      (f) => f.order == state.selectedFrameIndex
    );
  };

  // 現在選択中のオブジェクト
  const selectObject = (object: IEditableObject | undefined) => {
    state.selectedObject = object;
  };

  // オブジェクト操作用関数
  const doEditObject = (
    f: (_: IEditableObject) => void,
    object?: IEditableObject
  ) => {
    // オブジェクトの指定があったらそれに対して処理、なかったら選択オブジェクトに処理
    const targetObject = object ?? state.selectedObject;
    if (targetObject === undefined) return;
    f(targetObject);
  };
  // 選択中フレームに対する操作用関数
  const doSelectedFrameWithEditor = (f: (_: IFrameWithEditor) => void) => {
    if (state.selectedFrame === undefined) {
      return;
    }
    const editFrame = state.frameWithEdit.find(
      // コマ入れ替え機能によりidは重複する場合があるのでorderで検索する
      (x) => x.frame.order == state.selectedFrame?.order
    );
    if (editFrame === undefined) {
      return;
    }
    f(editFrame);
  };
  // 選択中フレームに対する操作用関数
  const doSelectedFrame = (f: (_: IEditFrame) => void) => {
    doSelectedFrameWithEditor((fe) => {
      if (fe.edit === undefined) {
        return;
      }
      f(fe.edit);
    });
  };
  // 選択中フレームに対する操作用関数
  const doSelectedFrameObjects = (f: (_: IEditableObject[]) => void) => {
    doSelectedFrame((frame) => f(frame.items));
  };
  // 選択オブジェクトを吹き出しなしにする
  const setBalloonOn = (isBalloonOn: boolean) => {
    doSelectedFrame((frame) => {
      const frameIndex = state.selectedFrameIndex;
      undo.add(
        "setBalloonOn",
        () => {
          selectFrame(frameIndex);
          frame.isBalloonOn = isBalloonOn;
        },
        () => {
          frame.isBalloonOn = !isBalloonOn;
          selectFrame(frameIndex);
        }
      );
    });
  };

  //////// 選択オブジェクトに対する操作群 Undo/Redo対象
  // 選択中フレームにオブジェクトを追加
  const addObject = (object: IEditableObject) => {
    doSelectedFrameObjects((xs) => {
      // 最大登録数制限
      if (consts.maxEditObjectCount <= xs.length) {
        alert(consts.maxEditObjectCountOver);
        return;
      }

      const frameIndex = state.selectedFrameIndex;
      undo.add(
        "add",
        () => {
          selectFrame(frameIndex);
          xs.push(object);
        },
        () => {
          selectFrame(frameIndex);
          const findIndex = xs.indexOf(object);
          if (findIndex < 0) return;
          xs.splice(findIndex, 1);
        },
        object
      );
      state.selectedObject = object;
    });
  };
  // セリフオブジェクトの追加
  const addText = (text: string, fontSize: string, lineHeight: number) => {
    if (!text) {
      text = consts.hiddenString;
    }
    text = `<p><span style="font-size: ${fontSize};">${text}</span></p>`;

    const object: ITextObject = {
      key: uuid().toString(),
      x: 0.5,
      y: 0.5,
      scale: 1,
      rotate: 0,
      content: text,
      isVertical: true,
      lineHeight: lineHeight,
    };
    addObject(object);
  };
  // 画像オブジェクトの追加
  const addImage = (image: IImageMaterial, x?: number, y?: number) => {
    const object: IImageObject = {
      key: uuid().toString(),
      x: x ?? 0.5,
      y: y ?? 0.5,
      scale: 1,
      rotate: 0,
      image: image,
    };
    addObject(object);
  };
  // 選択オブジェクトの削除
  const deleteObject = (object: IEditableObject) => {
    if (state.selectedObject !== object) {
      return;
    }
    doSelectedFrameObjects((xs) => {
      if (xs === undefined) {
        return;
      }
      const frameIndex = state.selectedFrameIndex;
      undo.add(
        "delete",
        () => {
          selectFrame(frameIndex);
          const targetIndex = xs.indexOf(object);
          if (0 <= targetIndex) {
            xs.splice(targetIndex, 1);
            state.selectedObject = undefined;
          }
        },
        () => {
          xs.push(object);
          state.selectedObject = object;
          selectFrame(frameIndex);
        },
        object
      );
    });
  };
  // 選択オブジェクトを縦書きにする
  const setVertical = (isVertical: boolean) => {
    doEditObject((o) => {
      if (isText(o)) {
        const frameIndex = state.selectedFrameIndex;
        undo.add(
          "vertical",
          () => {
            selectFrame(frameIndex);
            (o as ITextObject).isVertical = isVertical;
          },
          () => {
            (o as ITextObject).isVertical = !isVertical;
            selectFrame(frameIndex);
          },
          o
        );
      }
    });
  };
  // 選択オブジェクトのテキストを更新
  const setText = (content: string, object?: IEditableObject) => {
    doEditObject((o) => {
      if (isText(o)) {
        const beforeContent = (o as ITextObject).content;
        // 内容に変化がなければ何もしない
        if (content == beforeContent) return;
        const frameIndex = state.selectedFrameIndex;
        undo.add(
          "text",
          () => {
            selectFrame(frameIndex);
            (o as ITextObject).content = content;
          },
          () => {
            (o as ITextObject).content = beforeContent;
            selectFrame(frameIndex);
          },
          o
        );
      }
    }, object);
  };
  // 選択オブジェクトの行間を更新
  const lineHeight = (v: number, object?: IEditableObject) => {
    doEditObject((o) => {
      if (isText(o)) {
        const beforeLineHeight = (o as ITextObject).lineHeight;
        const frameIndex = state.selectedFrameIndex;
        undo.add(
          "lineHeight",
          () => {
            selectFrame(frameIndex);
            (o as ITextObject).lineHeight = v;
          },
          () => {
            (o as ITextObject).lineHeight = beforeLineHeight;
            selectFrame(frameIndex);
          },
          o
        );
      }
    }, object);
  };
  // 選択オブジェクトの回転角度を更新
  const rotate = (v: number, object?: IEditableObject) => {
    doEditObject((o) => {
      const beforeRotate = o.rotate;
      const frameIndex = state.selectedFrameIndex;
      undo.add(
        "rotate",
        () => {
          selectFrame(frameIndex);
          o.rotate = v;
        },
        () => {
          o.rotate = beforeRotate;
          selectFrame(frameIndex);
        },
        o
      );
    }, object);
  };
  // 選択オブジェクトのスケールを更新
  const scale = (v: number, object?: IEditableObject) => {
    doEditObject((o) => {
      const beforeScale = o.scale;
      const frameIndex = state.selectedFrameIndex;
      undo.add(
        "scale",
        () => {
          selectFrame(frameIndex);
          o.scale = v;
        },
        () => {
          o.scale = beforeScale;
          selectFrame(frameIndex);
        },
        o
      );
    }, object);
  };
  // 選択オブジェクトの位置を更新
  const moveObject = (x: number, y: number, object?: IEditableObject) => {
    doEditObject((o) => {
      const beforeX = o.x;
      const beforeY = o.y;
      const frameIndex = state.selectedFrameIndex;
      undo.add(
        "move",
        () => {
          selectFrame(frameIndex);
          o.x = x;
          o.y = y;
        },
        () => {
          o.x = beforeX;
          o.y = beforeY;
          selectFrame(frameIndex);
        },
        o
      );
    }, object);
  };
  const isTextObjectSelected = () =>
    state.selectedObject != undefined && isText(state.selectedObject);
  // 選択オブジェクトのテキスト書式を変更
  const formatDoc = (sCmd: string, sValue: string) => {
    if (!isTextObjectSelected()) return;
    // FIXME こいつだけ毛色が違う
    document.execCommand("styleWithCSS", false, "true");
    document.execCommand(sCmd, false, sValue);
  };
  const selectionGuard = (
    selected: () => void,
    unselected?: (_: Element) => void
  ) => {
    if (!isTextObjectSelected()) return;

    doSelectedFrameWithEditor((fe) => {
      // 選択オブジェクトのエレメントを取得
      if (!fe.editor) return;
      const selectedElement = fe.editor.getSelectedElement();
      if (!selectedElement) return;

      // contenteditableな要素を取得
      const textObjectElement = selectedElement?.querySelector(".content");
      if (!textObjectElement) return;

      // 選択状態かどうかで呼び出し先を分離
      const sel = window.getSelection();
      if (sel == null) return;
      const isEditing = (textObjectElement as HTMLElement).isContentEditable;
      const isNotSelected = sel.type == "Caret" || !isEditing;
      if (isNotSelected) {
        if (unselected !== undefined) {
          unselected(textObjectElement);
        }
      } else {
        selected();
      }
    });
  };

  // 子要素にわたってfont-sizeを初期化する
  const replaceFontSize = (n: Node, size: string, replace: string) => {
    const style = (n as HTMLElement).style;
    if (style && style.fontSize == size) {
      style.fontSize = replace;
    }
    n.childNodes.forEach((element) => {
      replaceFontSize(element, size, replace);
    });
  };

  const setToChildSpans = (
    n: Node,
    setter: (style: CSSStyleDeclaration) => void
  ) => {
    if (n.nodeName == "SPAN") {
      const style = (n as HTMLElement).style;
      if (style) {
        setter(style);
      }
    }
    n.childNodes.forEach((element) => {
      setToChildSpans(element, setter);
    });
  };
  const setFontSizeToChildSpans = (n: Node, size: string) => {
    setToChildSpans(n, (style) => (style.fontSize = size));
  };
  const setForeColorToChildSpans = (n: Node, color: string) => {
    setToChildSpans(n, (style) => (style.color = color));
  };
  const setBackColorToChildSpans = (n: Node, color: string) => {
    setToChildSpans(n, (style) => (style.backgroundColor = color));
  };

  // span要素取得
  const getToChildSpans = (
    n: Node,
    getter: (style: CSSStyleDeclaration) => void
  ): void => {
    if (n.nodeName == "SPAN") {
      const style = (n as HTMLElement).style;
      if (style) {
        getter(style);
      }
    }
    n.childNodes.forEach((element) => {
      getToChildSpans(element, getter);
    });
  };
  // 最大フォントサイズの取得
  const getMaxFontSizeToChildSpans = (maxFontSize: string, n: Node): string => {
    getToChildSpans(n, (style) => {
      if (style.fontSize > maxFontSize) {
        maxFontSize = style.fontSize;
      }
    });
    return maxFontSize;
  };

  // font-sizeの変更
  // 没1. document.execCommand("fontSize", false, "5");
  //    execCommandではpx単位で指定できないのでダメ
  // 没2. Range.cloneContents()で選択範囲をspanでwrapしてdocument.execCommand("insertHTML", false, span.innerHTML);
  //    テキスト全体を範囲選択して実行した時におかしな挙動になるのでダメ
  // 採3. document.execCommand("fontSize", false, "7");実行後、設定されるfontsize="xxx-large"の値を狙って置換する
  const setFontSize = (value: string): string => {
    let maxFontSize = value;
    selectionGuard(
      () => {
        // 選択範囲を取得
        const sels = window.getSelection();
        if (!sels) return;
        if (!sels.anchorNode) return;
        // 最初にfontsize="xxx-large"を設定
        formatDoc("fontSize", consts.fonthackResolveMagicValue);
        // fontsize="xxx-large"を設定したいサイズに置換
        const editableElem = findParent(sels.anchorNode, (node) => {
          return (node as HTMLElement).contentEditable == "true";
        });
        if (!editableElem) return;
        replaceFontSize(editableElem, consts.fonthackResolveMagicSize, value);
        // 最大フォントサイズを取得する
        maxFontSize = getMaxFontSizeToChildSpans(maxFontSize, editableElem);
        setText((editableElem as HTMLElement).innerHTML);
      },
      (textObjectElement) => {
        // ブラウザの選択がない時は選択オブジェクトを対象にする
        if (!isText(state.selectedObject)) return;
        setFontSizeToChildSpans(textObjectElement, value);
        // 最大フォントサイズを取得する
        maxFontSize = getMaxFontSizeToChildSpans(
          maxFontSize,
          textObjectElement
        );
        setText(textObjectElement.innerHTML);
      }
    );
    return maxFontSize;
  };

  // 文字色を更新
  const setFontColor = (value: string) =>
    selectionGuard(
      () => formatDoc("forecolor", value),
      (textObjectElement) => {
        // ブラウザの選択がない時は選択オブジェクトを対象にする
        if (!isText(state.selectedObject)) return;
        setForeColorToChildSpans(textObjectElement, value);
        setText(textObjectElement.innerHTML);
      }
    );

  // マーカー色を更新
  const setHiliteColor = (value: string) =>
    selectionGuard(
      () => formatDoc("hiliteColor", value),
      (textObjectElement) => {
        // ブラウザの選択がない時は選択オブジェクトを対象にする
        if (!isText(state.selectedObject)) return;
        setBackColorToChildSpans(textObjectElement, value);
        setText(textObjectElement.innerHTML);
      }
    );
  // 選択オブジェクトのタイトルを更新
  const setTitle = (title: string) => {
    if (!title) {
      title = "無題";
    }
    state.editData.title = title;
  };

  // 作業状態を保存
  const saveData = async (title: string) => {
    const currentTitle = state.editData.title;
    setTitle(title);
    // タイトル変えてたら別名で保存
    const isSaveAs = currentTitle != state.editData.title;
    if (isSaveAs) {
      state.editData.id = undefined;
      state.editData.frames.forEach((frame) => {
        frame.id = undefined;
        frame.items.forEach((obj) => {
          obj.id = undefined;
        });
      });
      // コマ入れ替えデータの場合はeditsetも新規生成させるため初期化する
      if (state.editData.editset != undefined) {
        state.editData.editset.id = undefined;
        state.editData.editset.editDataID = undefined;
        state.editData.editset.thumbnailPath = undefined;
      }
    }

    const result = await resource.saveEditData(state.editData);
    if (result !== undefined) {
      updateEditState(result);
    }
    return result;
  };

  // 広告素材のオブジェクトを削除
  const clearObjects = (predicate: (_: IEditableObject) => boolean) => {
    // 編集中オブジェクトから削除
    state.editData.frames.forEach((frame) =>
      // FIXME こっちにすべき
      // frame.items.filter(predicate).forEach((item) => {
      frame.items.forEach((item) => {
        if (predicate(item)) {
          const targetIndex = frame.items.indexOf(item);
          if (0 <= targetIndex) {
            frame.items.splice(targetIndex, 1);
          }
        }
      })
    );
    // undo履歴からも削除オブジェクトに関する履歴をクリア
    undo.clear((_, tag) => {
      if (!tag) return false;
      if (!isImage(tag)) return false;
      return predicate(tag);
    });
    state.selectedObject = undefined;
  };

  // ドラッグ＆ドロップによるコマ入れ替え時の編集データ変更処理
  const changeFrameWithEdit = (newValue: IFrameWithEditor[], index: number) => {
    // コマ入れ替え後の新しいselectedFrameIndexを事前に求めておく
    const newSelectedFrameIndex = newValue?.findIndex((f) => f.index == index);
    // 入れ替えたコマを事前に求めておく
    const newSelectedFrame = newValue?.find((f) => f.index == index);
    // editAreaの参照場所は順序変更しないため退避しておく
    const tmpEditArea: IEditor[] = [];
    state.frameWithEdit.forEach((x) => {
      if (x.editor != undefined) {
        tmpEditArea.push(x.editor);
      }
    });

    // 入れ替え済みデータのindexおよびorderを昇順で更新する
    newValue.forEach((x, index) => {
      x.index = index;
      x.frame.order = index;
      x.edit.order = index;
    });
    // 更新
    state.frameWithEdit = newValue;
    // selectedFrameIndexを更新
    state.selectedFrameIndex = newSelectedFrameIndex;
    // 編集中コマを入れ替えたコマで更新
    state.selectedFrame = newSelectedFrame?.frame;
    state.selectedEdit = newSelectedFrame?.edit;
    // editAreaの参照場所を復帰
    tmpEditArea.forEach((x, index) => {
      state.frameWithEdit[index].editor = x;
    });

    // undoをクリア
    undo.clear(() => {
      return true;
    });
  };

  // コマ入れ替え時の編集データ変更処理
  const changeFrame = (selectFrame: ITemplateImage) => {
    // 入れ替え対象のコマ順序
    const targetOrder = state.selectedFrameIndex;
    selectFrame.order = targetOrder;

    // テンプレートの入れ替え
    let index = 0;
    index = state.template.frames.findIndex((f) => f.order == targetOrder);
    state.template.frames[index] = selectFrame;
    state.template.tags = [];
    state.template.describe = "";
    state.template.thumbnail = "";

    // 編集データの入れ替え
    index = state.editData.frames.findIndex((f) => f.order == targetOrder);
    state.editData.frames[index].templateImageID = selectFrame.id;

    // 編集アイテムをクリア
    state.editData.frames[index].items.length = 0;

    // 吹き出しをON状態に
    state.editData.frames[index].isBalloonOn = true;

    // 表示用コマデータの入れ替え
    index = targetOrder;
    state.frameWithEdit[index].frame = selectFrame;
    state.selectedFrame = selectFrame;

    // コマ入れ替え編集データに切替
    state.editData.templateID = undefined;
    if (state.editData.editset == undefined) {
      state.editData.editset = emptyEditSet();
    }

    // undoをクリア
    undo.clear(() => {
      return true;
    });
  };

  return {
    state,
    setEditor,
    setTitle,
    selectFrame,
    selectObject,
    deleteObject,
    addText,
    addImage,
    rotate,
    scale,
    lineHeight,
    moveObject,
    setVertical,
    setText,
    setFontSize,
    setFontColor,
    setHiliteColor,
    saveData,
    setBalloonOn,
    clearObjects,
    changeFrameWithEdit,
    changeFrame,
  };
};

export type EditStore = ReturnType<typeof useEditStore>;
